import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import { pushSupported, pushGranted, enablePush } from '../utils/push';

const typeIcon = { registration_open: '📝', results_published: '🏆' };

const NotificationBell = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const [pushReady, setPushReady] = useState(pushGranted());
  const ref = useRef(null);

  const showPushCTA = pushSupported() && !pushReady && Notification.permission !== 'denied';

  const handleEnablePush = async () => {
    try {
      await enablePush(API);
      setPushReady(true);
      toast.success('התראות פוש הופעלו! תקבל עדכונים גם כשהאתר סגור 🔔');
    } catch (e) {
      if (e.message === 'denied') toast.error('ההרשאה נדחתה. ניתן לאפשר בהגדרות הדפדפן.');
      else toast.error('לא ניתן להפעיל התראות במכשיר זה');
    }
  };

  const authHeaders = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchNotifs = async () => {
    try {
      const r = await axios.get(`${API}/notifications`, authHeaders());
      setItems(r.data.notifications || []);
      setUnread(r.data.unread_count || 0);
    } catch (e) { /* ignore */ }
    try {
      const m = await axios.get(`${API}/messages/unread-count`, authHeaders());
      setMsgUnread(m.data?.unread_count || 0);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchNotifs();
    const i = setInterval(fetchNotifs, 60000);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [API]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await axios.post(`${API}/notifications/mark-read`, {}, authHeaders()); setUnread(0); } catch (e) { /* ignore */ }
    }
  };

  const go = (link) => { setOpen(false); if (link) navigate(link); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggleOpen}
        data-testid="notif-bell-btn"
        aria-label="התראות"
        className="relative p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {(unread + msgUnread) > 0 ? (
          <span
            data-testid="notif-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center ring-2 ring-white"
          >
            {(unread + msgUnread) > 9 ? '9+' : unread + msgUnread}
          </span>
        ) : showPushCTA ? (
          <span
            data-testid="notif-push-dot"
            className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full ring-2 ring-white animate-pulse"
          ></span>
        ) : null}
      </button>

      {open && (
        <div
          data-testid="notif-dropdown"
          className="absolute left-0 mt-2 w-80 max-w-[90vw] bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-[70]"
          dir="rtl"
        >
          <div className="px-4 py-2 border-b border-gray-100">
            <span className="font-bold text-gray-800">התראות</span>
          </div>
          {showPushCTA && (
            <button
              onClick={handleEnablePush}
              data-testid="enable-push-btn"
              className="w-full text-right flex items-center gap-3 px-4 py-3 bg-gradient-to-l from-blue-50 to-emerald-50 hover:from-blue-100 hover:to-emerald-100 transition-colors border-b border-gray-100"
            >
              <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1d6ff2] to-cyan-400 flex items-center justify-center shrink-0">
                <Bell className="w-5 h-5 text-white" />
              </span>
              <span className="min-w-0">
                <span className="block font-black text-sm text-[#1e2b5e]">הפעל התראות פוש 🔔</span>
                <span className="block text-xs text-gray-500">קבל עדכונים על תחרויות והודעות גם כשהאתר סגור</span>
              </span>
            </button>
          )}
          {msgUnread > 0 && (
            <button
              onClick={() => go('/messages')}
              data-testid="notif-messages-row"
              className="w-full text-right flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100"
            >
              <span className="text-xl shrink-0">✉️</span>
              <span className="min-w-0 flex-1">
                <span className="block font-bold text-sm text-gray-900">יש לך {msgUnread} הודעות חדשות בצ'אט</span>
                <span className="block text-xs text-gray-500">לחץ לצפייה בשיחות</span>
              </span>
              <span className="min-w-[20px] h-5 px-1.5 bg-[#1d6ff2] text-white text-[11px] font-black rounded-full flex items-center justify-center shrink-0">
                {msgUnread > 99 ? '99+' : msgUnread}
              </span>
            </button>
          )}
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-400 text-sm">אין התראות חדשות</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.link)}
                  data-testid={`notif-item-${n.id}`}
                  className="w-full text-right flex gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                >
                  <span className="text-xl shrink-0">{typeIcon[n.type] || '🔔'}</span>
                  <div className="min-w-0">
                    <div className="font-bold text-sm text-gray-900 truncate">{n.title}</div>
                    <div className="text-xs text-gray-500 line-clamp-2">{n.body}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
